import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, CheckCircle, Flag, UserX, UserPlus, UserCheck } from 'lucide-react';
import { useUser } from '../../contexts/UserContext';
import { supabase } from '../../lib/supabase';
import ReportModal from '../common/ReportModal';
import BadgeIcon from '../common/BadgeIcon';
import TeamTag from '../common/TeamTag';
import { roleLabels, isDspRole } from '../../lib/roleLabels';
import { interestLabels } from '../../lib/interests';

const cardStyle = {
  backgroundColor: 'var(--card-bg)',
  border: '1px solid var(--card-border)',
};


const headerStyle = {
  backgroundColor: 'var(--header-bg)',
  borderBottom: '1px solid rgba(255,255,255,0.1)',
  paddingTop: 'var(--sat)',
};


const avatarColors = [
  'from-brand-primary to-brand-secondary',
  'from-emerald-500 to-teal-500',
  'from-violet-500 to-purple-500',
  'from-orange-400 to-rose-400',
];

const formatMemberSince = (isoString) => {
  if (!isoString) return '';
  return new Date(isoString).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

const PublicProfileScreen = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { session } = useUser();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [badgesData, setBadgesData] = useState(null);
  const [statsData, setStatsData] = useState(null);
  const [gamificationLoading, setGamificationLoading] = useState(true);
  const [activeBadgeId, setActiveBadgeId] = useState(null);

  // Moderation: block + report
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockConfirm, setBlockConfirm] = useState(false);
  const [blockBusy, setBlockBusy] = useState(false);
  const [showReport, setShowReport] = useState(false);

  // Follow
  const [isFollowing, setIsFollowing] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);

  // Redirect to own profile if viewing self
  useEffect(() => {
    if (session?.user?.id && userId === session.user.id) {
      navigate('/profile', { replace: true });
    }
  }, [userId, session, navigate]);

  // Gamification data — badges + stats
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      const [badgesResult, statsResult] = await Promise.all([
        supabase.rpc('get_user_badges', { p_user_id: userId }),
        supabase.rpc('get_user_stats',  { p_user_id: userId }),
      ]);
      if (cancelled) return;
      if (!badgesResult.error) setBadgesData(badgesResult.data);
      if (!statsResult.error)  setStatsData(statsResult.data?.[0] ?? null);
      setGamificationLoading(false);
    })();
    return () => { cancelled = true; };
  }, [userId]);

  // Current block + follow state. Both tables have owner-read RLS that narrows
  // the visible set to the caller's own rows BEFORE these filters apply, so
  // neither select can reach another user's relationships.
  useEffect(() => {
    if (!userId || !session?.user?.id) return;
    let cancelled = false;
    (async () => {
      const [blockRes, followRes] = await Promise.all([
        supabase.from('user_blocks').select('blocked_id').eq('blocked_id', userId).maybeSingle(),
        supabase.from('user_follows').select('followee_id').eq('followee_id', userId).maybeSingle(),
      ]);
      if (cancelled) return;
      setIsBlocked(!!blockRes.data);
      setIsFollowing(!!followRes.data);
    })();
    return () => { cancelled = true; };
  }, [userId, session?.user?.id]);

  const handleToggleBlock = async () => {
    if (blockBusy) return;
    setBlockBusy(true);
    const { data, error: blockError } = await supabase.rpc('toggle_block_user', { p_blocked_id: userId });
    setBlockBusy(false);
    setBlockConfirm(false);
    if (!blockError) {
      setIsBlocked(data === true);
      // A block severs the follow graph both ways server-side (027) — mirror it
      // locally so the button doesn't claim a relationship that no longer exists.
      // Their follower count only drops if WE were one of their followers.
      if (data === true && isFollowing) {
        setIsFollowing(false);
        setStatsData(s => (s ? { ...s, follower_count: Math.max(Number(s.follower_count) - 1, 0) } : s));
      }
    }
  };

  const handleToggleFollow = async () => {
    if (followBusy) return;
    setFollowBusy(true);
    const next = !isFollowing;
    // Optimistic: flip the button and nudge the count so the tile doesn't lie
    // until the next fetch.
    setIsFollowing(next);
    setStatsData(s => (s ? { ...s, follower_count: Number(s.follower_count) + (next ? 1 : -1) } : s));

    const { data, error: followError } = await supabase.rpc('toggle_follow_user', { p_followee_id: userId });
    setFollowBusy(false);

    if (followError) {
      console.error('toggle_follow_user:', followError);
      setIsFollowing(!next);
      setStatsData(s => (s ? { ...s, follower_count: Number(s.follower_count) + (next ? -1 : 1) } : s));
    } else {
      setIsFollowing(data === true);
    }
  };

  // Fetch public profile
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      const { data, error: rpcError } = await supabase.rpc('get_public_profile', { p_user_id: userId });
      if (cancelled) return;
      if (rpcError || !data || data.length === 0) {
        setError(true);
      } else {
        setProfile(data[0]);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/feed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pb-nav">
        <div className="sticky top-0 z-20 md:hidden" style={headerStyle}>
          <div className="max-w-2xl mx-auto px-4 py-2">
            <div className="h-6 w-24 rounded animate-pulse" style={{ backgroundColor: 'rgba(255,255,255,0.25)' }} />
          </div>
        </div>
        <div className="max-w-2xl mx-auto px-4 py-5 space-y-4">
          <div className="rounded-xl overflow-hidden animate-pulse" style={cardStyle}>
            <div className="h-28" style={{ backgroundColor: 'var(--skeleton-bg)' }} />
            <div className="px-5 pb-5 -mt-12">
              <div className="w-24 h-24 rounded-full mb-3" style={{ backgroundColor: 'var(--skeleton-bg)' }} />
              <div className="h-5 w-1/3 rounded mb-2" style={{ backgroundColor: 'var(--skeleton-bg)' }} />
              <div className="h-3 w-1/2 rounded" style={{ backgroundColor: 'var(--skeleton-bg)' }} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen pb-nav">
        <div className="sticky top-0 z-20 md:hidden" style={headerStyle}>
          <div className="max-w-2xl mx-auto px-4 py-2">
            <button onClick={handleBack} className="flex items-center gap-1 text-sm text-white/70 hover:text-white transition-colors">
              <ChevronLeft size={18} />
              Back
            </button>
          </div>
        </div>
        <div className="hidden md:block max-w-2xl mx-auto px-4 pt-5">
          <button onClick={handleBack} className="flex items-center gap-1 text-sm hover:opacity-70 transition-opacity" style={{ color: 'var(--text-secondary)' }}>
            <ChevronLeft size={16} />
            Back
          </button>
        </div>
        <div className="max-w-2xl mx-auto px-4 py-12 text-center">
          <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>Profile not found.</p>
        </div>
      </div>
    );
  }

  const displayName = (profile.use_username && profile.username) ? profile.username : (profile.name || 'DSP Member');
  const avatarColor = avatarColors[(displayName.charCodeAt(0) || 0) % avatarColors.length];
  const roleDisplay = roleLabels[profile.role] || profile.role || null;
  const yearsDisplay = profile.years_experience != null
    ? `${profile.years_experience} ${profile.years_experience === 1 ? 'year' : 'years'}`
    : null;
  const subtitleParts = [roleDisplay, yearsDisplay, profile.agency].filter(Boolean);
  const subtitle = subtitleParts.join(' · ');

  return (
    <div className="min-h-screen pb-nav">
      {/* Mobile sticky header */}
      <div className="sticky top-0 z-20 md:hidden" style={headerStyle}>
        <div className="max-w-2xl mx-auto px-4 py-2 flex items-center gap-3">
          <button onClick={handleBack} aria-label="Back" className="text-white/70 hover:text-white transition-colors flex-shrink-0">
            <ChevronLeft size={22} />
          </button>
          <h1 className="text-lg font-bold text-white truncate">{displayName}</h1>
        </div>
      </div>

      {/* Desktop back button */}
      <div className="hidden md:block max-w-2xl mx-auto px-4 pt-5 pb-2">
        <button onClick={handleBack} className="flex items-center gap-1 text-sm hover:opacity-70 transition-opacity" style={{ color: 'var(--text-secondary)' }}>
          <ChevronLeft size={16} />
          Back
        </button>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-5 space-y-4">

        {/* Profile hero */}
        <div className="rounded-xl overflow-hidden" style={cardStyle}>
          <div
            className="relative h-28"
            style={{ background: 'linear-gradient(135deg, var(--brand-primary) 0%, var(--brand-secondary) 50%, #4DAAAA 100%)' }}
          >
            <div className="absolute inset-0" style={{ background: 'radial-gradient(circle at 30% 50%, rgba(255,255,255,0.15) 0%, transparent 60%)' }} />
          </div>
          <div className="px-5 pb-5 -mt-12 relative">
            {/* Avatar */}
            <div className="relative w-24 mb-3">
              {profile.avatar_url ? (
                <img
                  src={profile.avatar_url}
                  alt={displayName}
                  className="w-24 h-24 rounded-full object-cover border-4 shadow-lg"
                  style={{ borderColor: 'var(--card-bg)' }}
                />
              ) : (
                <div
                  className={`w-24 h-24 rounded-full border-4 shadow-lg flex items-center justify-center bg-gradient-to-br ${avatarColor}`}
                  style={{ borderColor: 'var(--card-bg)' }}
                >
                  <span className="text-3xl font-bold text-white">{displayName.charAt(0).toUpperCase()}</span>
                </div>
              )}
            </div>

            {/* Name + verified badge */}
            <div className="flex items-center gap-2 min-w-0 mb-0.5">
              <h2 className="text-xl font-bold text-primary truncate">{displayName}</h2>
              <TeamTag userId={profile.id} />
              {/* 048: verification reads as DSP status only for DSP roles. A
                  verified non-DSP (agency staff, a moderator) can still post;
                  they just aren't labelled a DSP. */}
              {profile.verified && isDspRole(profile.role) && (
                <div
                  className="flex items-center gap-1 px-2 py-0.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: 'rgba(var(--brand-primary-rgb, 91, 140, 111), 0.15)' }}
                >
                  <CheckCircle size={14} style={{ color: 'var(--brand-primary)' }} />
                  <span className="text-xs font-medium" style={{ color: 'var(--brand-primary)' }}>Verified DSP</span>
                </div>
              )}
              {/* Only DSP roles show "Not verified": for family members or
                  agency staff it would read as a mark against people who are
                  welcome here; their role label already says who they are. */}
              {profile.verified === false && isDspRole(profile.role) && (
                <span
                  className="px-2 py-0.5 rounded-full text-xs font-medium text-tertiary flex-shrink-0"
                  style={{ backgroundColor: 'var(--pill-bg)', border: '1px solid var(--pill-border)' }}
                >
                  Not verified
                </span>
              )}
            </div>

            {/* Username handle (always shown if set) */}
            {profile.username && (
              <p className="text-xs mb-0.5" style={{ color: 'var(--text-tertiary)' }}>@{profile.username}</p>
            )}

            {/* Follower counts. Deliberately TEXT, not buttons — tappable counts
                imply a browsable list, and follower lists are private (027). */}
            {!gamificationLoading && statsData && (
              <p className="text-xs mb-1" style={{ color: 'var(--text-tertiary)' }}>
                <span className="font-semibold text-secondary">{statsData.follower_count ?? 0}</span>
                {' '}Followers
                <span className="mx-1.5">·</span>
                <span className="font-semibold text-secondary">{statsData.following_count ?? 0}</span>
                {' '}Following
              </p>
            )}

            {/* Role · years · agency */}
            {subtitle && <p className="text-sm text-tertiary mb-2">{subtitle}</p>}

            {/* Bio */}
            {profile.bio && (
              <p className="text-sm text-secondary leading-relaxed mb-2">{profile.bio}</p>
            )}

            {/* Member since */}
            <p className="text-xs text-tertiary mb-3">Member since {formatMemberSince(profile.created_at)}</p>

            {/* Block / Report actions (never on own profile) */}
            {session?.user?.id && session.user.id !== userId && (
              <div className="mb-3">
                {blockConfirm ? (
                  <div
                    className="flex flex-col gap-2 p-3 rounded-xl"
                    style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--card-border)' }}
                  >
                    <p className="text-xs text-secondary">
                      Block {displayName}? You won't see their posts, replies, or videos.
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={handleToggleBlock}
                        disabled={blockBusy}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition-all hover:brightness-110 active:scale-[0.98] disabled:opacity-50"
                        style={{ backgroundColor: '#dc2626' }}
                      >
                        {blockBusy ? 'Blocking…' : 'Block'}
                      </button>
                      <button
                        onClick={() => setBlockConfirm(false)}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold text-secondary transition-all active:scale-[0.98]"
                        style={{ backgroundColor: 'var(--pill-bg)', border: '1px solid var(--pill-border)' }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    {/* Follow is the primary affordance, so it gets fill while
                        Block/Report stay neutral pills. Hidden entirely when
                        blocked — toggle_follow_user would raise 'blocked'. */}
                    {!isBlocked && (
                      <button
                        onClick={handleToggleFollow}
                        disabled={followBusy}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:brightness-110 active:scale-[0.98] disabled:opacity-50"
                        style={
                          isFollowing
                            ? { backgroundColor: 'var(--pill-bg)', border: '1px solid var(--pill-border)', color: 'var(--brand-primary)' }
                            : { backgroundColor: 'var(--brand-primary)', border: '1px solid var(--brand-primary)', color: '#fff' }
                        }
                      >
                        {isFollowing ? <UserCheck size={14} /> : <UserPlus size={14} />}
                        {isFollowing ? 'Following' : 'Follow'}
                      </button>
                    )}
                    <button
                      onClick={() => (isBlocked ? handleToggleBlock() : setBlockConfirm(true))}
                      disabled={blockBusy}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:brightness-110 active:scale-[0.98] disabled:opacity-50"
                      style={{ backgroundColor: 'var(--pill-bg)', border: '1px solid var(--pill-border)', color: isBlocked ? 'var(--brand-primary)' : 'var(--text-secondary)' }}
                    >
                      <UserX size={14} />
                      {isBlocked ? 'Unblock' : 'Block'}
                    </button>
                    <button
                      onClick={() => setShowReport(true)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-secondary transition-all hover:brightness-110 active:scale-[0.98]"
                      style={{ backgroundColor: 'var(--pill-bg)', border: '1px solid var(--pill-border)' }}
                    >
                      <Flag size={14} />
                      Report
                    </button>
                    {isBlocked && (
                      <span className="text-xs text-tertiary">You've blocked this user</span>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Stats */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { value: gamificationLoading || !statsData ? '—' : String(statsData.post_count),       label: 'Posts',         color: 'var(--brand-primary)' },
                { value: gamificationLoading || !statsData ? '—' : String(statsData.tables_joined),    label: 'Tables Joined', color: '#9B7EC8' },
                { value: gamificationLoading || !statsData ? '—' : String(statsData.helpful_received), label: 'Helpful Votes', color: '#4DAAAA' },
              ].map(stat => (
                <div
                  key={stat.label}
                  className="text-center py-2.5 rounded-lg"
                  style={{ backgroundColor: 'var(--pill-bg)', border: '1px solid var(--pill-border)' }}
                >
                  <div className="text-xl font-bold" style={{ color: stat.color }}>{stat.value}</div>
                  <div className="text-xs text-tertiary">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Interests */}
        {profile.interests && profile.interests.length > 0 && (
          <div className="rounded-xl p-4" style={cardStyle}>
            <h3 className="font-semibold text-sm text-primary mb-3">Interests</h3>
            <div className="flex flex-wrap gap-2">
              {profile.interests.map(interest => (
                <span
                  key={interest}
                  className="px-3 py-1 rounded-full text-xs font-medium"
                  style={{ backgroundColor: 'var(--pill-bg)', color: 'var(--text-secondary)', border: '1px solid var(--pill-border)' }}
                >
                  {interestLabels[interest] || interest}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Badges */}
        <div className="rounded-xl p-4" style={cardStyle}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-sm text-primary">Badges</h3>
            {!gamificationLoading && badgesData && (
              <span className="text-xs text-tertiary">
                {badgesData.filter(b => b.earned).length}/{badgesData.length} earned
              </span>
            )}
          </div>
          {gamificationLoading ? (
            <div className="grid grid-cols-3 gap-2">
              {[...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className="h-16 rounded-lg animate-pulse"
                  style={{ backgroundColor: 'var(--pill-bg)' }}
                />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {(badgesData ?? []).map(badge => (
                <button
                  key={badge.id}
                  onClick={() => setActiveBadgeId(activeBadgeId === badge.id ? null : badge.id)}
                  className="flex flex-col items-center gap-1.5 py-2.5 rounded-lg transition-all"
                  style={{
                    backgroundColor: 'var(--pill-bg)',
                    opacity: badge.earned ? 1 : 0.4,
                  }}
                >
                  <BadgeIcon badge={badge} />
                  <span className="text-xs text-tertiary font-medium text-center leading-tight px-1">
                    {badge.label}
                  </span>
                  {activeBadgeId === badge.id && (
                    <span className="text-xs text-center leading-tight px-2 pb-1" style={{ color: 'var(--text-tertiary)' }}>
                      {badge.earned ? badge.description : badge.how_to_earn}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

      </div>

      {showReport && (
        <ReportModal contentType="profile" contentId={userId} onClose={() => setShowReport(false)} />
      )}
    </div>
  );
};

export default PublicProfileScreen;
